import { useState, useRef, useCallback, useEffect } from 'react';

// Types
interface SpeechRecognitionEvent extends Event {
  results: {
    isFinal: boolean;
    [index: number]: { transcript: string };
    length: number;
  }[];
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

type SpeechRecognition = EventTarget & {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: ((e: Event) => void) | null;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEvent) => void) | null;
  onend: ((e: Event) => void) | null;
};

// Global flag - once native fails with network error, never try again
let nativePermanentlyDisabled = false;

export function useHybridSpeechRecognition(language = 'es') {
  // State
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentMode, setCurrentMode] = useState<'native' | 'openai' | null>(null);

  // Refs
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const wasManuallyStoppedRef = useRef(false);
  const audioChunksRef = useRef<Blob[]>([]);
  const nativeResultRef = useRef('');

  // Check support
  const hasNativeSupport = typeof window !== 'undefined' && 
    ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);

  // ============ NATIVE MODE ============
  const startNative = useCallback(() => {
    if (!hasNativeSupport || nativePermanentlyDisabled) {
      return false;
    }

    // Clean up any previous
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }

    console.log('[STT] Starting NATIVE mode');
    wasManuallyStoppedRef.current = false;
    
    const SpeechRecognitionAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognitionAPI() as SpeechRecognition;
    recognitionRef.current = recognition;

    recognition.lang = language === 'es' ? 'es-MX' : language;
    recognition.continuous = false;
    recognition.interimResults = true;

    let hasResult = false;
    let hasNetworkError = false;

    recognition.onstart = () => {
      console.log('[STT] Native: started');
      setCurrentMode('native');
      setIsListening(true);
      setIsProcessing(false);
      setError(null);
      setTranscript('');
      nativeResultRef.current = '';
      hasResult = false;
      hasNetworkError = false;
      wasManuallyStoppedRef.current = false;
    };

    recognition.onresult = (e: SpeechRecognitionEvent) => {
      let final = '';
      let interim = '';
      
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) {
          final += r[0].transcript;
          hasResult = true;
        } else {
          interim += r[0].transcript;
        }
      }

      if (final) nativeResultRef.current += final;
      setTranscript(nativeResultRef.current + interim);
    };

    recognition.onerror = (e: SpeechRecognitionErrorEvent) => {
      console.warn('[STT] Native error:', e.error);
      
      // Network error means native is not available (firewall, no internet, etc)
      if (e.error === 'network') {
        hasNetworkError = true;
        nativePermanentlyDisabled = true;
      } else if (e.error === 'not-allowed') {
        setError('Permiso de micrófono denegado');
      }
      // no-speech and others are not fatal, just let onend handle it
    };

    recognition.onend = () => {
      console.log('[STT] Native ended. Result:', hasResult, 'NetworkError:', hasNetworkError);
      
      recognitionRef.current = null;
      
      // Case 1: We have a result - success!
      if (hasResult && nativeResultRef.current) {
        setIsListening(false);
        setCurrentMode(null);
        return;
      }
      
      // Case 2: Network error - silently fallback to OpenAI
      if (hasNetworkError) {
        console.log('[STT] Native network error, falling back to OpenAI silently');
        // Don't show error, don't change listening state, just switch modes
        startOpenAI(true); // true = isFallback
        return;
      }
      
      // Case 3: Manual stop or no speech - just stop
      setIsListening(false);
      setCurrentMode(null);
      
      if (!nativeResultRef.current) {
        setTranscript('');
      }
    };

    try {
      recognition.start();
      return true;
    } catch (err) {
      console.error('[STT] Native start failed:', err);
      nativePermanentlyDisabled = true;
      return false;
    }
  }, [hasNativeSupport, language]);

  // Stop native
  const stopNative = useCallback(() => {
    console.log('[STT] Stopping native');
    wasManuallyStoppedRef.current = true;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }
  }, []);

  // ============ OPENAI MODE ============
  const startOpenAI = useCallback(async (isFallback = false) => {
    if (mediaRecorderRef.current) {
      console.log('[STT] OpenAI: Already active');
      return;
    }

    console.log('[STT] Starting OPENAI mode', isFallback ? '(fallback)' : '');
    
    // If it's a fallback from native, keep isListening true for smooth transition
    if (!isFallback) {
      setIsListening(true);
      setTranscript('');
    }
    
    setCurrentMode('openai');
    setIsProcessing(true);
    setError(null);
    audioChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : 'audio/mp4';
      
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data?.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        console.log('[STT] OpenAI: Processing...');
        setIsProcessing(true);
        setIsListening(false);

        try {
          const blob = new Blob(audioChunksRef.current, { type: mimeType });
          const formData = new FormData();
          formData.append('audio', blob, 'recording.webm');
          formData.append('language', language);

          const token = localStorage.getItem('token') || localStorage.getItem('ares_token');
          
          const res = await fetch('/ares/transcribe', {
            method: 'POST',
            body: formData,
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          });

          if (res.status === 401) {
            throw new Error('Sesión expirada');
          }
          if (!res.ok) {
            throw new Error(`Error ${res.status}`);
          }

          const data = await res.json();
          if (data.success && data.text) {
            setTranscript(data.text);
          } else {
            throw new Error(data.error || 'Error en transcripción');
          }
        } catch (err) {
          console.error('[STT] OpenAI error:', err);
          setError(err instanceof Error ? err.message : 'Error');
        } finally {
          stream.getTracks().forEach(t => t.stop());
          mediaRecorderRef.current = null;
          setIsProcessing(false);
          setIsListening(false);
          setCurrentMode(null);
        }
      };

      recorder.onerror = () => {
        stream.getTracks().forEach(t => t.stop());
        mediaRecorderRef.current = null;
        setError('Error grabando audio');
        setIsListening(false);
        setIsProcessing(false);
        setCurrentMode(null);
      };

      recorder.start(100);
      setIsProcessing(false);
      
    } catch (err) {
      setError('No se pudo acceder al micrófono');
      setIsListening(false);
      setIsProcessing(false);
      setCurrentMode(null);
    }
  }, [language]);

  // Stop OpenAI
  const stopOpenAI = useCallback(() => {
    console.log('[STT] Stopping OpenAI');
    if (mediaRecorderRef.current) {
      try { 
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch {}
    }
  }, []);

  // ============ PUBLIC API ============
  const startListening = useCallback(async () => {
    // Already active? Ignore
    if (isListening || isProcessing) {
      console.log('[STT] Already active, ignoring');
      return;
    }

    console.log('[STT] Start listening. Native disabled:', nativePermanentlyDisabled);
    
    // Try native first (if never failed before)
    if (!nativePermanentlyDisabled) {
      const started = startNative();
      if (started) return;
    }
    
    // Use OpenAI
    await startOpenAI();
  }, [isListening, isProcessing, startNative, startOpenAI]);

  const stopListening = useCallback(() => {
    console.log('[STT] Stop listening. Mode:', currentMode);
    
    if (currentMode === 'native') {
      stopNative();
    } else if (currentMode === 'openai') {
      stopOpenAI();
    } else {
      // Fallback: try to stop whatever is active
      stopNative();
      stopOpenAI();
    }
  }, [currentMode, stopNative, stopOpenAI]);

  const clearTranscript = useCallback(() => {
    setTranscript('');
    nativeResultRef.current = '';
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch {}
      }
      if (mediaRecorderRef.current) {
        try { 
          if (mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
          }
        } catch {}
      }
    };
  }, []);

  return {
    isListening,
    isLoading: isProcessing,
    transcript,
    error,
    startListening,
    stopListening,
    clearTranscript,
    mode: currentMode,
    label: currentMode === 'native' ? 'Voz nativa' : currentMode === 'openai' ? 'Voz cloud' : '',
  };
}

export default useHybridSpeechRecognition;
